import * as React from 'react'

import moment from 'moment'

import { WeeklyRestEventModel } from '@/app/models/weeklyRestModel'
import { DATE_FORMAT } from '@/app/constants/date'

import { useScene } from '../context/SceneContext'

const color = '#22B14C'

export type RestPartBlockProps = {
  weeklyRest: WeeklyRestEventModel
  index: number
}

const prepareTooltipInfo = (
  weeklyRest: WeeklyRestEventModel,
): WeeklyRestEventModel => ({
  ...weeklyRest,
  typeName: 'eventName.weeklyRest',
  color,
})


const ArrowIcon: React.FC<{
  left: number
  height: number
  onTheRight: boolean
  tooltip: string
}> = ({ left, height, onTheRight, tooltip }) => {
  const image = onTheRight ? 'rightArrowLightGreen' : 'leftArrowLightGreen'
  return (
    <div
      className="tw-flex tw-flex-col tw-items-start tw-justify-start"
      style={{ position: 'absolute', left, top: 15, bottom: 0 }}
    >
      <div
        style={{
          borderColor: '#000000',
          width: 30,
          height: height / 4,
          pointerEvents: 'auto',
        }}
        data-tip={tooltip}
        data-for="weeklyRestTooltip"
      >
        <img
          src={`/assets/icons_nucleo/icons/mix/${image}.svg`}
          width={20}
          height={15}
        />
      </div>
    </div>
  )
}

export const RestPartBlock: React.FC<RestPartBlockProps> = React.memo(
  (props: RestPartBlockProps) => {
    const scene = useScene()
    const HEIGHT = scene.height - 54
    const weeklyRest = props.weeklyRest

    const startTime = scene.startTime

 
    if (scene.days > 1) {
      const tooltip = JSON.stringify(prepareTooltipInfo(weeklyRest))
      const arrows: React.ReactNode[] = []

      for (let d = 0; d < scene.days; d++) {
        const dayStart = startTime.clone().add(d, 'days')
        const dayEnd = dayStart.clone().add(1, 'days')
        const weekAfterDay = dayStart.clone().add(7, 'days')

        const overlaps =
          moment(weeklyRest.startTime).isBefore(dayEnd) &&
          moment(weeklyRest.endTime).isAfter(dayStart)
        if (!overlaps) continue

        const endsWithinWeek =
          moment(weeklyRest.formalRestEndDate).isBefore(weekAfterDay)
        const isStartDay =
          moment(weeklyRest.startTime).isAfter(dayStart) && endsWithinWeek
        const isFdowDay =
          endsWithinWeek &&
          moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(
            DATE_FORMAT,
          ) === moment(dayStart).format(DATE_FORMAT)
        if (isStartDay || isFdowDay) continue

        const onTheRight =
          moment(weeklyRest.firstDayOfWeekTheRestBelongsTo) > moment(dayStart)

        arrows.push(
          <ArrowIcon
            key={d}
            left={scene.timeToX(dayStart)}
            height={HEIGHT}
            onTheRight={onTheRight}
            tooltip={tooltip}
          />,
        )
      }

      return <>{arrows}</>
    }

    const oneWeek = startTime.clone().add(7, 'days')
    const twoWeeks = startTime.clone().add(14, 'days')

    let leftTime: any = weeklyRest.startTime

    if (
      moment(weeklyRest.startTime) > moment(startTime) &&
      moment(weeklyRest.formalRestEndDate) < moment(oneWeek)
    ) {
      return null
    }

    if (
      moment(weeklyRest.startTime) > moment(oneWeek) &&
      moment(weeklyRest.formalRestEndDate) < moment(twoWeeks)
    ) {
      return null
    }

    if (
      moment(weeklyRest.formalRestEndDate) < moment(twoWeeks) &&
      moment(weeklyRest.formalRestEndDate) > moment(oneWeek) &&
      moment(weeklyRest.startTime) < moment(oneWeek)
    ) {
      leftTime = oneWeek.clone().add(-6, 'hours')
    }
    if (
      moment(weeklyRest.formalRestEndDate) > moment(twoWeeks) &&
      moment(weeklyRest.formalRestEndDate) > moment(oneWeek) &&
      moment(weeklyRest.startTime) > moment(oneWeek)
    ) {
      leftTime = twoWeeks.clone().add(-6, 'hours')
    }

    if (
      moment(weeklyRest.formalRestEndDate) < moment(oneWeek) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(startTime).format(DATE_FORMAT)
    ) {
      return null
    }

    if (
      moment(weeklyRest.formalRestEndDate) > moment(oneWeek) &&
      moment(weeklyRest.formalRestEndDate) < moment(twoWeeks) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(startTime).format(DATE_FORMAT)
    ) {
      return null
    }

    if (
      moment(weeklyRest.formalRestEndDate) > moment(twoWeeks) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(oneWeek).format(DATE_FORMAT)
    ) {
      return null
    }

    const left = scene.timeToX(leftTime)
    const onTheRight =
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo) > moment(startTime)
    const image = onTheRight ? 'rightArrowLightGreen' : 'leftArrowLightGreen'

    return (
      <div
        className="tw-flex tw-flex-col tw-items-start tw-justify-start"
        style={{
          position: 'absolute',
          left,
          top: 15,
          bottom: 0,
        }}
      >
        <div
          style={{
            borderColor: '#000000',
            width: 30,
            height: HEIGHT / 4,
            pointerEvents: 'auto',
          }}
          data-tip={JSON.stringify(prepareTooltipInfo(weeklyRest))}
          data-for="weeklyRestTooltip"
        >
          <img
            src={`/assets/icons_nucleo/icons/mix/${image}.svg`}
              width={20}
          height={15}
          />
        </div>
      </div>
    )
  },
)

RestPartBlock.displayName = 'RestPartBlock'
